export interface CityData {
  name: string;
  lat: number;
  lng: number;
}

export interface LocationData {
  slug: string;
  name: string;
  state: string;
  region: string;
  description: string;
  highlights: string[];
  content: string[];
  isPrimary: boolean;
  lat: number;
  lng: number;
  cities: CityData[];
}

/**
 * The fourteen towns EC Design and Construction LLC listed as its service
 * areas. Clifton is the headquarters and the map's center point.
 */
export const locations: LocationData[] = [
  {
    slug: "clifton",
    name: "Clifton",
    state: "NJ",
    region: "Passaic County",
    isPrimary: true,
    lat: 40.8584,
    lng: -74.1638,
    description:
      "EC Design and Construction LLC is based in Clifton, New Jersey. We handle home remodeling, painting, masonry, roofing, retaining walls, pavers, flooring and new construction for homeowners and property owners across Clifton and the surrounding towns, Monday through Saturday.",
    highlights: [
      "Based in Clifton, NJ",
      "Monday–Saturday, 8 AM – 5 PM",
      "Remodeling, masonry, roofing & more",
      "Free estimates in English & Spanish",
    ],
    content: [
      "Clifton is home for us. Our base sits between Botany Village and Athenia, and most of our work starts within a few minutes of it. The city's housing stock is a real mix — pre-war two-families near Main Avenue, post-war capes and colonials through Richfield and Delawanna, and the larger properties up toward Montclair Heights — and each of those brings its own remodeling and repair problems.",
      "The older housing here means a lot of what we do is catching up on deferred work: replacing roofs that have been patched one too many times, repointing brick steps and stoops that have taken twenty New Jersey winters, opening up closed-off kitchens, and finishing basements that were never more than storage. We also do new paver patios and driveways, retaining walls on the sloped lots near Garret Mountain, and full interior repaints.",
      "Because we're local, a Clifton estimate usually means we can be at your property quickly and start sooner. Call EC Design and Construction LLC at (973) 931-5980 or request a free estimate online — we work in English and Spanish.",
    ],
    cities: [
      { name: "Botany Village", lat: 40.8683, lng: -74.1499 },
      { name: "Athenia", lat: 40.8747, lng: -74.1583 },
      { name: "Allwood", lat: 40.8517, lng: -74.1786 },
      { name: "Richfield", lat: 40.8367, lng: -74.1653 },
    ],
  },
  {
    slug: "passaic",
    name: "Passaic",
    state: "NJ",
    region: "Passaic County",
    isPrimary: false,
    lat: 40.8568,
    lng: -74.1285,
    description:
      "Remodeling, masonry, roofing and painting in Passaic, NJ. EC Design and Construction LLC works on homes, multi-family properties and small commercial spaces throughout Passaic — free estimates, English and Spanish.",
    highlights: [
      "Home & multi-family remodeling",
      "Roofing and masonry repairs",
      "Interior & exterior painting",
      "Free estimates, no obligation",
    ],
    content: [
      "Passaic is dense, and most of the buildings are old — which makes it one of the busiest towns we work in. Two- and three-family houses around the Third Ward and Dundee Island were built for a different era, and updating a kitchen or bathroom in one of them usually means dealing with the framing, plumbing chases and wiring behind the walls, not just the finishes.",
      "We do a lot of roofing and masonry here for that reason. Flat and low-slope roofs, chimney repointing, brick stoops and retaining walls that have shifted, and water-damage repairs where a roof or gutter has been failing quietly for years. On the interior side it's kitchen and bath remodels, basement finishing, drywall and full repaints — for owner-occupants and landlords preparing a unit for the next tenant.",
      "We're minutes away in Clifton, and we speak Spanish as well as English. Call (973) 931-5980 for a free estimate on your Passaic property.",
    ],
    cities: [
      { name: "Downtown Passaic", lat: 40.8568, lng: -74.1285 },
      { name: "Dundee Island", lat: 40.8656, lng: -74.1189 },
      { name: "Passaic Park", lat: 40.8455, lng: -74.1339 },
      { name: "Third Ward", lat: 40.862, lng: -74.124 },
    ],
  },
  {
    slug: "paterson",
    name: "Paterson",
    state: "NJ",
    region: "Passaic County",
    isPrimary: false,
    lat: 40.9168,
    lng: -74.1718,
    description:
      "Construction and remodeling services in Paterson, NJ — home remodeling, roofing, masonry, flooring, painting and structural repairs for houses and multi-family properties across the city. Free estimates from EC Design and Construction LLC.",
    highlights: [
      "Remodeling & structural repairs",
      "Roofing, flooring and masonry",
      "Multi-family and rental work",
      "English & Spanish service",
    ],
    content: [
      "Paterson has some of the oldest housing in the state, and working here means knowing how those buildings were actually put together. Around the Great Falls district and the Eastside you'll find balloon-framed houses, brick row buildings, and additions layered on over a century. We frame, repair and remodel in all of it.",
      "Common jobs for us in Paterson: full roof tear-offs and replacements, repointing and rebuilding brick walls, steps and chimneys, replacing rotted framing and headers, opening up interior walls, and gut renovations of kitchens and bathrooms. Landlords call us for turnovers — drywall, paint, flooring and the repairs that come to light once a unit is empty.",
      "We give straight answers on whether something needs a repair or a replacement. Call EC Design and Construction LLC at (973) 931-5980 for a free estimate anywhere in Paterson.",
    ],
    cities: [
      { name: "Great Falls District", lat: 40.916, lng: -74.181 },
      { name: "Eastside", lat: 40.921, lng: -74.147 },
      { name: "Hillcrest", lat: 40.928, lng: -74.165 },
      { name: "Riverside", lat: 40.933, lng: -74.158 },
    ],
  },
  {
    slug: "wayne",
    name: "Wayne",
    state: "NJ",
    region: "Passaic County",
    isPrimary: false,
    lat: 40.9253,
    lng: -74.2765,
    description:
      "Home remodeling, additions, pavers, retaining walls and roofing in Wayne, NJ. EC Design and Construction LLC serves Packanack Lake, Preakness, Pines Lake and the rest of the township. Free estimates.",
    highlights: [
      "Kitchen, bath & basement remodels",
      "Paver patios and walkways",
      "Retaining walls on sloped lots",
      "Additions and flooring",
    ],
    content: [
      "Wayne is a township of single-family homes on real lots, and that changes the kind of work we do here. Instead of tight multi-family renovations it's additions, kitchen and bathroom remodels, finished basements, and a lot of exterior work — paver patios, walkways, driveways and retaining walls.",
      "The lake neighborhoods, Packanack and Pines Lake especially, have plenty of sloped and irregular lots. Those are the properties where a retaining wall has to be built properly — compacted base, drainage stone, correct backfill — or it leans within a few seasons. We also see a lot of mid-century split-levels and ranches ready for their first serious interior update, plus roof replacements on homes hitting the twenty-five year mark.",
      "If you're planning an addition or reworking your outdoor space, we can walk the property and price it out. Call (973) 931-5980 for a free estimate in Wayne.",
    ],
    cities: [
      { name: "Packanack Lake", lat: 40.933, lng: -74.253 },
      { name: "Preakness", lat: 40.913, lng: -74.236 },
      { name: "Pines Lake", lat: 40.945, lng: -74.268 },
      { name: "Mountain View", lat: 40.907, lng: -74.296 },
    ],
  },
  {
    slug: "garfield",
    name: "Garfield",
    state: "NJ",
    region: "Bergen County",
    isPrimary: false,
    lat: 40.8815,
    lng: -74.1132,
    description:
      "Remodeling, roofing, masonry and painting in Garfield, NJ. EC Design and Construction LLC handles kitchen and bath renovations, roof replacements, brick and concrete work and full interior repaints. Free estimates.",
    highlights: [
      "Kitchen & bathroom remodeling",
      "Roof replacement and repair",
      "Brick, block and concrete work",
      "Interior & exterior painting",
    ],
    content: [
      "Garfield sits right across the river from Passaic and Clifton, so it's very much part of our home territory. The housing is mostly older one- and two-family homes on compact lots — Belmont Hill, Plauderville, the streets off Outwater Lane — with the maintenance needs that come with buildings of that age.",
      "The work here tends to be roofs, masonry and interiors. We replace shingle roofs and repair flashing and valleys, repoint and rebuild brick steps, porches and chimneys, pour and replace concrete walkways, and remodel kitchens and bathrooms that haven't been touched in decades. Painting is a steady request too, inside and out.",
      "Free estimates, in English or Spanish. Call EC Design and Construction LLC at (973) 931-5980 to schedule a walkthrough in Garfield.",
    ],
    cities: [
      { name: "Plauderville", lat: 40.888, lng: -74.108 },
      { name: "Belmont Hill", lat: 40.876, lng: -74.117 },
      { name: "Dahnerts Lake", lat: 40.883, lng: -74.104 },
      { name: "Outwater Lane", lat: 40.879, lng: -74.121 },
    ],
  },
  {
    slug: "fair-lawn",
    name: "Fair Lawn",
    state: "NJ",
    region: "Bergen County",
    isPrimary: false,
    lat: 40.9404,
    lng: -74.1318,
    description:
      "Home remodeling, additions, pavers and roofing in Fair Lawn, NJ. EC Design and Construction LLC works throughout Radburn, Warren Point, Memorial Park and the surrounding neighborhoods. Free estimates.",
    highlights: [
      "Kitchen, bath and basement remodels",
      "Paver patios and front walkways",
      "Roofing and exterior repairs",
      "Framing for additions",
    ],
    content: [
      "Fair Lawn is a borough of well-kept single-family homes, and the projects follow from that: owners who plan to stay put and want the house to work better. Kitchen and bathroom remodels, opening up a wall between kitchen and living room, finishing a basement, and adding usable outdoor space.",
      "Radburn's early planned-community houses and the post-war capes and colonials through Warren Point and Memorial Park are solid buildings that reward good work. We frame additions and dormers, replace roofs, install paver patios and walkways, build retaining walls where the grade calls for it, and handle interior and exterior painting.",
      "We'll come out, measure, and give you a written estimate at no cost. Call (973) 931-5980 to set up a visit in Fair Lawn.",
    ],
    cities: [
      { name: "Radburn", lat: 40.937, lng: -74.123 },
      { name: "Warren Point", lat: 40.928, lng: -74.118 },
      { name: "Memorial Park", lat: 40.943, lng: -74.133 },
      { name: "Columbia Heights", lat: 40.949, lng: -74.14 },
    ],
  },
  {
    slug: "haledon",
    name: "Haledon",
    state: "NJ",
    region: "Passaic County",
    isPrimary: false,
    lat: 40.9359,
    lng: -74.1885,
    description:
      "Construction and remodeling in Haledon, NJ — roofing, masonry, retaining walls, remodeling and painting for homes across the borough. Free estimates from EC Design and Construction LLC.",
    highlights: [
      "Roofing and gutter-related repairs",
      "Retaining walls on hillside lots",
      "Kitchen and bathroom remodeling",
      "Masonry steps, stoops and walkways",
    ],
    content: [
      "Haledon is small and hilly, tucked between Paterson and North Haledon, and the terrain drives a lot of what gets built here. Properties running up toward High Mountain Road sit on real grade, which means retaining walls, stepped walkways and drainage that has to be thought through rather than guessed at.",
      "Alongside that we do the usual homeowner work — roof replacements, masonry repairs to steps and stoops, kitchen and bathroom remodels, basement finishing, and interior and exterior painting. The housing is largely older single-family and two-family homes, so structural repairs and framing work come up regularly once walls are opened.",
      "We're a short drive away in Clifton. Call EC Design and Construction LLC at (973) 931-5980 for a free estimate in Haledon.",
    ],
    cities: [
      { name: "Belmont Avenue", lat: 40.933, lng: -74.186 },
      { name: "Haledon Avenue", lat: 40.939, lng: -74.19 },
      { name: "High Mountain Road", lat: 40.945, lng: -74.195 },
      { name: "Zeliff Avenue", lat: 40.931, lng: -74.193 },
    ],
  },
  {
    slug: "pompton-plains",
    name: "Pompton Plains",
    state: "NJ",
    region: "Morris County",
    isPrimary: false,
    lat: 40.9679,
    lng: -74.306,
    description:
      "Remodeling, additions, pavers, retaining walls and roofing in Pompton Plains, NJ. EC Design and Construction LLC serves Pequannock Township and the surrounding area. Free estimates.",
    highlights: [
      "Additions and flooring",
      "Paver patios and driveways",
      "Retaining walls and grading",
      "Full home remodeling",
    ],
    content: [
      "Pompton Plains, in Pequannock Township, is the western edge of where we regularly work. It's a quieter, more suburban area of single-family homes on generous lots, and the projects reflect that — additions, garage and basement conversions, kitchen and bath renovations, and substantial outdoor work.",
      "Paver patios and driveways are popular here, and so are retaining walls, since a number of properties near the Pompton and Pequannock rivers have grade and drainage to manage. We also handle roofing, siding and exterior repairs, framing for additions and dormers, and interior remodeling from demolition through finish carpentry.",
      "Call (973) 931-5980 to talk through your project and schedule a free on-site estimate in Pompton Plains.",
    ],
    cities: [
      { name: "Jackson Avenue", lat: 40.97, lng: -74.301 },
      { name: "Newark Pompton Tpke", lat: 40.964, lng: -74.309 },
      { name: "Pequannock Township", lat: 40.977, lng: -74.305 },
      { name: "Lincoln Park Border", lat: 40.953, lng: -74.302 },
    ],
  },
  {
    slug: "midland-park",
    name: "Midland Park",
    state: "NJ",
    region: "Bergen County",
    isPrimary: false,
    lat: 40.9987,
    lng: -74.1418,
    description:
      "Home remodeling, roofing, masonry and pavers in Midland Park, NJ. EC Design and Construction LLC handles interior renovations and exterior improvements throughout the borough. Free estimates.",
    highlights: [
      "Kitchen and bathroom remodeling",
      "Roof replacement and repair",
      "Paver walkways and patios",
      "Masonry and concrete work",
    ],
    content: [
      "Midland Park is a compact Bergen County borough of single-family homes, and the work here is mostly homeowners investing in a house they intend to keep. Kitchen and bathroom remodels, finished basements, and updates to layouts that were designed for how people lived fifty years ago.",
      "On the exterior we install paver walkways, patios and driveways, rebuild masonry steps and porches, pour concrete, and replace roofs. Painting — interior repaints and full exterior jobs — rounds out most of what we're called for in the borough.",
      "You'll get a written estimate before anything is scheduled. Call EC Design and Construction LLC at (973) 931-5980 for a free walkthrough in Midland Park.",
    ],
    cities: [
      { name: "Godwin Avenue", lat: 40.996, lng: -74.145 },
      { name: "Franklin Avenue", lat: 41.001, lng: -74.139 },
      { name: "Dairy Street", lat: 40.995, lng: -74.136 },
      { name: "Wortendyke", lat: 41.003, lng: -74.147 },
    ],
  },
  {
    slug: "ridgewood",
    name: "Ridgewood",
    state: "NJ",
    region: "Bergen County",
    isPrimary: false,
    lat: 40.9793,
    lng: -74.1165,
    description:
      "Remodeling, additions, masonry and roofing in Ridgewood, NJ. EC Design and Construction LLC works on the village's older homes — kitchens, baths, flooring, brick and stone work and paver patios. Free estimates.",
    highlights: [
      "Whole-home and kitchen remodeling",
      "Additions, dormers and flooring",
      "Brick, stone and paver work",
      "Roofing and exterior restoration",
    ],
    content: [
      "Ridgewood has some of the best older housing stock in Bergen County — center-hall colonials, Tudors and Victorians, many of them close to a century old. Renovating them properly means matching what's already there rather than fighting it, whether that's trim profiles, brick and stone, or the framing behind a wall you're about to open.",
      "We take on kitchen and bathroom remodels, additions and dormers, basement finishing, and structural repairs. On the exterior it's masonry — steps, stoops, chimneys, stone veneer and repointing — plus paver patios and walkways, retaining walls, roof replacements and full exterior painting.",
      "Free written estimates, no obligation. Call (973) 931-5980 to schedule a visit anywhere in Ridgewood.",
    ],
    cities: [
      { name: "Downtown Ridgewood", lat: 40.9793, lng: -74.1165 },
      { name: "Willard", lat: 40.988, lng: -74.108 },
      { name: "Somerville", lat: 40.97, lng: -74.129 },
      { name: "Upper Ridgewood", lat: 40.993, lng: -74.118 },
    ],
  },
  {
    slug: "wallington",
    name: "Wallington",
    state: "NJ",
    region: "Bergen County",
    isPrimary: false,
    lat: 40.8523,
    lng: -74.1071,
    description:
      "Remodeling, roofing, masonry and painting in Wallington, NJ. EC Design and Construction LLC serves the borough's one- and two-family homes with renovations, repairs and exterior work. Free estimates.",
    highlights: [
      "One- and two-family remodeling",
      "Roofing and flashing repairs",
      "Masonry steps, stoops and walkways",
      "Interior & exterior painting",
    ],
    content: [
      "Wallington is a small, densely built borough just across the Passaic River from Passaic and Garfield, and it's an easy run for our crews. Most of the housing is older one- and two-family homes on narrow lots, well maintained but reaching the age where roofs, masonry and mechanicals all come due around the same time.",
      "We handle roof replacements and repairs, brick and concrete work on steps, stoops and walkways, kitchen and bathroom remodeling, basement finishing, and interior and exterior painting. For landlords we do full unit turnovers — drywall, floors, paint and the repairs that surface between tenants.",
      "Call EC Design and Construction LLC at (973) 931-5980 for a free estimate in Wallington. We work in English and Spanish.",
    ],
    cities: [
      { name: "Main Avenue", lat: 40.853, lng: -74.109 },
      { name: "Paterson Avenue", lat: 40.856, lng: -74.103 },
      { name: "Union Boulevard", lat: 40.848, lng: -74.112 },
      { name: "Locust Avenue", lat: 40.85, lng: -74.105 },
    ],
  },
  {
    slug: "bloomfield",
    name: "Bloomfield",
    state: "NJ",
    region: "Essex County",
    isPrimary: false,
    lat: 40.8068,
    lng: -74.1854,
    description:
      "Home remodeling, roofing, masonry and painting in Bloomfield, NJ. EC Design and Construction LLC renovates kitchens, baths and basements and handles exterior work across the township. Free estimates.",
    highlights: [
      "Kitchen, bath & basement remodeling",
      "Roof replacement and repair",
      "Masonry and retaining walls",
      "Framing and structural repairs",
    ],
    content: [
      "Bloomfield runs from Watsessing up to Brookdale, and the housing changes as you go — older two-families and multi-family buildings toward the south end, larger single-family colonials and tudors up north. We work across both, which means one week is a rental turnover and the next is a full kitchen renovation.",
      "Typical Bloomfield projects for us: gut kitchen and bathroom remodels, finished basements, framing and structural repairs once walls come open, roof replacements, and masonry work on steps, porches and retaining walls. Interior and exterior painting is a constant on houses of this age.",
      "We're about fifteen minutes up the road in Clifton. Call (973) 931-5980 for a free estimate in Bloomfield.",
    ],
    cities: [
      { name: "Bloomfield Center", lat: 40.8068, lng: -74.1854 },
      { name: "Brookdale", lat: 40.825, lng: -74.193 },
      { name: "Watsessing", lat: 40.795, lng: -74.183 },
      { name: "Broad Street", lat: 40.811, lng: -74.188 },
    ],
  },
  {
    slug: "montclair",
    name: "Montclair",
    state: "NJ",
    region: "Essex County",
    isPrimary: false,
    lat: 40.8259,
    lng: -74.209,
    description:
      "Remodeling, additions, masonry, pavers and roofing in Montclair, NJ. EC Design and Construction LLC works on the township's older homes from Upper Montclair to the South End. Free estimates.",
    highlights: [
      "Historic-home remodeling",
      "Stone and brick masonry",
      "Retaining walls and paver patios",
      "Additions, dormers and flooring",
    ],
    content: [
      "Montclair is built on a hillside, and it shows in the work. Between Upper Montclair and the South End you have Victorians, craftsman houses, stone-front colonials and a lot of properties with serious grade behind them — which is why masonry and retaining walls come up here more than almost anywhere else we work.",
      "We build and rebuild stone and brick walls, steps and stoops, install paver patios and walkways on sloped lots, and handle drainage that has to be right the first time. Inside, it's kitchen and bathroom remodeling, basement finishing, framing for additions and dormers, and the structural repairs that older houses hide behind plaster.",
      "Call EC Design and Construction LLC at (973) 931-5980 for a free estimate in Montclair — we'll walk the property with you before quoting.",
    ],
    cities: [
      { name: "Upper Montclair", lat: 40.846, lng: -74.201 },
      { name: "Watchung Plaza", lat: 40.833, lng: -74.206 },
      { name: "Montclair Center", lat: 40.813, lng: -74.21 },
      { name: "South End", lat: 40.8, lng: -74.217 },
    ],
  },
  {
    slug: "woodland-park",
    name: "Woodland Park",
    state: "NJ",
    region: "Passaic County",
    isPrimary: false,
    lat: 40.8901,
    lng: -74.1954,
    description:
      "Construction and remodeling in Woodland Park, NJ — remodeling, roofing, masonry, retaining walls and pavers for homes throughout the borough. Free estimates from EC Design and Construction LLC.",
    highlights: [
      "Home remodeling and finishing",
      "Retaining walls on graded lots",
      "Roofing and exterior repairs",
      "Paver patios and driveways",
    ],
    content: [
      "Woodland Park sits along the western slope of Garret Mountain, right next door to us in Clifton. The grade is the defining feature — plenty of properties along Rifle Camp Road and up toward Great Notch are built into a hill, so retaining walls, stepped walkways and drainage work are a regular part of what we do here.",
      "Beyond that it's standard homeowner work done properly: kitchen and bathroom remodels, basement finishing, roof replacements, paver patios and driveways, masonry repairs to steps and stoops, and interior and exterior painting. Split-levels and ranches from the sixties and seventies make up much of the borough, and most are ready for their second real renovation.",
      "We're just minutes away. Call (973) 931-5980 for a free estimate in Woodland Park.",
    ],
    cities: [
      { name: "Rifle Camp Road", lat: 40.883, lng: -74.188 },
      { name: "McBride Avenue", lat: 40.888, lng: -74.199 },
      { name: "Garret Mountain", lat: 40.883, lng: -74.174 },
      { name: "Great Notch", lat: 40.876, lng: -74.21 },
    ],
  },
];

export const primaryLocation = locations.find((l) => l.isPrimary) ?? locations[0];
